<template>
  <div class="relative m-3 p-4 flex items-center justify-between
  w-1/2 bg-gray-100 mx-auto rounded-2xl select-none">
    <div class="flex gap-8 items-center">
      <CardLabel :cardLabelText="this.$t(this.getSectionHeaderKey())" :bold="true"/>
      <div class="flex flex-col items-center cursor-default text-xs text-left w-auto gap-2 text-ellipsis overflow-hidden">
        <img v-if="this.section.icon"
          :src="sectionIcon(this.section.icon)"
          class="w-12 h-12"
          alt="Meta Object Icon" @click="copyMetaObjectId">
        <div class="flex flex-col gap-0">
          <p>{{ this.$t("popUpImage.from") }} <span class="font-bold">{{ this.section.locationAlias }}</span></p>
          <p>{{ this.$t("popUpImage.at") }} <span class="font-bold">{{ this.section.time }}</span></p>
        </div>
      </div>
      <div
        v-for="(valueElt, index) in this.section.values"
        :key="index"
        class="flex">
        <div class="flex items-center gap-2">
          <span v-if="valueElt.label" class="text-xs text-gray-500 min-w-[110px] text-left">{{ this.$t(valueElt.label) }}</span>
          <div contenteditable="true"
            @keydown="preventReturn"
            @blur="updateValue($event, index)"
            class="flex hover:bg-gray-200 cursor-pointer rounded-md p-1 -m-1"
            spellcheck="false">
            <i v-if="valueElt.edited">
              <svg width="12px" height="12px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.048"></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M13 21H21" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M20.0651 7.39423L7.09967 20.4114C6.72438 20.7882 6.21446 21 5.68265 21H4.00383C3.44943 21 3 20.5466 3
                  19.9922V18.2987C3 17.7696 3.20962 17.2621 3.58297 16.8873L16.5517 3.86681C19.5632 1.34721 22.5747 4.87462 20.0651
                  7.39423Z" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M15.3097 5.30981L18.7274 8.72755" stroke="#5e5e5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                </g>
              </svg>
            </i>
            {{ valueElt.value.replaceAll('_', ' ') ?? '' }}
          </div>
        </div>
        <img
          :src="confidenceIcon(valueElt.confidence)"
          v-on:mouseenter="confidenceHovered = index"
          v-on:mouseleave="confidenceHovered = -1"
          v-on:focusin="confidenceHovered = index"
          v-on:focusout="confidenceHovered = -1"
          :class="['w-5 h-5 self-center ml-2',
              this.confidenceHovered === index? 'scale-110' : '']"
          alt="confidence icon"
          :title=confidenceRounded(valueElt.confidence)>
      </div>
    </div>
    <div v-if="hasSomeImage" class="bg-gray-200 rounded-xl px-2 py-1
        transition-colors duration-200 ease-in-out transform hover:bg-gray-300 hover:shadow-lg
        hover:scale-105 w-fit"
        @click="openPopup()">
      <i class="w-8 h-8"><img :src="sectionIcon('forensicImageIcon')" class="w-8 h-8" alt="camera icon"></i>
    </div>
  </div>
  <PopupImage
    :show="showPopup"
    :transitSections="[this.section]"
    @close="showPopup = false" />
</template>

<script>
import useTransitStore from '@/stores/TransitStore';
import { VEHICLE_INFO_TYPES } from '@/utils/types';
import { getConfidenceIcon, getIconName, getIcon } from '@/utils/icons';
import valueToPercent from '@/utils/math';
import { getStatisticalTypeName } from '@/utils/lookup';
import { getLocalDateTimeFromUnixTimestamp } from '@/utils/date';

import PopupImage from '@/components/PopUpImage.vue';
import CardLabel from '@/components/CardLabel.vue';

export default {
  name: 'ObjectCard',
  props: ['metaObject'],
  components: {
    PopupImage,
    CardLabel,
  },
  setup() {
    const transitStore = useTransitStore();
    const confidenceIcon = getConfidenceIcon;
    const confidenceRounded = valueToPercent;
    const getStatsTypeName = getStatisticalTypeName;
    const sectionIcon = getIcon;
    return {
      transitStore,
      confidenceIcon,
      confidenceRounded,
      sectionIcon,
      getStatsTypeName,
      getLocalDateTimeFromUnixTimestamp,
    };
  },
  data() {
    return {
      vehicleInfoTypes: VEHICLE_INFO_TYPES,
      section: {},
      showPopup: false,
      confidenceHovered: -1,
    };
  },
  computed: {
    hasSomeImage() {
      return (this.section.images?.length > 0);
    },
  },
  methods: {
    async openPopup() {
      this.showPopup = true;
    },
    async updateValue(event, index) {
      const updatedValue = event.target.innerText;
      const currentSection = this.section.values[index];
      if (currentSection.value === updatedValue) return;
      let putResult = false;
      if (currentSection.db_id && currentSection.db_id !== -1) {
        if (currentSection.value_type === 'reading') {
          putResult = await this.transitStore.putReadingValue({ readingId: currentSection.db_id, newValue: updatedValue });
        } else {
          putResult = await this.transitStore.putClassificationValue({ classificationId: currentSection.db_id, newValue: updatedValue });
        }
      }
      if (putResult) {
        currentSection.edited = true;
        currentSection.value = updatedValue;
        this.emitter.emit('show-toast', { toastType: 'info', toastText: this.$t('transitCard.successfulEdit') });
      } else {
        const replaceValue = currentSection.value;
        currentSection.value = replaceValue;
        this.emitter.emit('show-toast', { toastType: 'error', toastText: this.$t('transitCard.errorOnEdit') });
      }
    },
    preventReturn(event) {
      const keyCode = (event.keyCode ? event.keyCode : event.which);
      if (keyCode === 13) {
        event.preventDefault();
        event.target.blur();
      }
    },
    // Resolves the i18n key for this section's panel header: either the
    // reader-specific override (see getIntermodalMarkingRailHeader) or the
    // generic stats-type name. Kept out of the template to stay under max-len.
    getSectionHeaderKey() {
      return this.section.customHeader ?? this.getStatsTypeName(this.section.type);
    },
    // TP-750 / Thiago's feedback: intermodal_marking_rail plates come in two
    // physically different layouts (EN 13044-2 Swap Body/Container vs EN 13044-3
    // Semi-Trailer), distinguishable by their classification type prefix
    // (cs_* vs pn_*, see ars/wrappers/intermodal_marking_rail.py). The panel
    // header must reflect whichever one was actually read, never a generic label.
    getIntermodalMarkingRailHeader(metaObject, type) {
      if (type !== 'intermodal_marking_rail') return null;
      const classificationTypes = metaObject?.classifications?.map((c) => c.type) ?? [];
      if (classificationTypes.some((t) => t?.startsWith('cs_'))) {
        return 'objectCard.intermodalMarkingRailSwapBodyHeader';
      }
      if (classificationTypes.some((t) => t?.startsWith('pn_'))) {
        return 'objectCard.intermodalMarkingRailSemiTrailerHeader';
      }
      return null;
    },
    // TP-750 / Thiago's field mapping table: fields 2-4 differ in *meaning*
    // between the two plate types (e.g. cs_box_width is a physical width,
    // pn_seating_device_height is a hitch height), so the label must be
    // resolved from the classification's own type, never from a fixed
    // 4-label template. The gauge code (field 1) is the plate's reading
    // itself (C+S or P + rail profile), not a classification. box_resistance
    // is genuinely the same "Structure Code" concept on both plate types.
    getIntermodalMarkingRailFieldLabel(type) {
      const labelKeyByType = {
        intermodal_marking_rail: 'objectCard.intermodalMarkingRailGaugeCode',
        cs_box_length: 'objectCard.intermodalMarkingRailLengthCode',
        cs_box_width: 'objectCard.intermodalMarkingRailMaxWidth',
        cs_box_resistance: 'objectCard.intermodalMarkingRailStructureCode',
        pn_seating_device_height: 'objectCard.intermodalMarkingRailHitchHeight',
        pn_box_resistance: 'objectCard.intermodalMarkingRailStructureCode',
        pn_compatibilities: 'objectCard.intermodalMarkingRailCompatibilityCode',
      };
      return labelKeyByType[type] ?? null;
    },
    setSectionTypeAndIcon(metaObject, section) {
      const newSection = { ...section };
      const readingType = metaObject?.reading?.type;
      const classificationType = metaObject?.classifications?.[0]?.type;
      const classificationValue = metaObject?.classifications?.[0]?.value;
      if (metaObject) {
        if (metaObject.child_meta_objects !== null
        && typeof metaObject.child_meta_objects !== 'undefined'
        && metaObject.child_meta_objects.length > 0) {
          metaObject.child_meta_objects.forEach((childMetaObject) => {
            newSection.childMetaObjects.push(this.getSection(childMetaObject));
          });
        }
      }
      newSection.type = readingType ?? classificationType ?? '';
      newSection.typeConfidence = metaObject?.reading?.confidence ?? metaObject?.classifications?.[0]?.confidence ?? '';
      newSection.icon = getIconName(newSection.type, classificationValue);
      newSection.customHeader = this.getIntermodalMarkingRailHeader(metaObject, newSection.type);
      if (newSection.type === 'vehicleclassification'
       || newSection.type === 'vehicledetection') newSection.type = 'vehicle';
      if (this.vehicleInfoTypes.includes(newSection.type)) newSection.type = 'vehicle information';
      if (newSection.type.includes('universal')) newSection.type = newSection.type.replace('universal', '');
      newSection.locationAlias = metaObject.location_alias ? metaObject.location_alias : metaObject.location_name;
      return newSection;
    },

    setSectionValues(metaObject, section) {
      const newSection = { ...section };
      newSection.section_id = metaObject.id;
      newSection.values = [];
      metaObject?.classifications?.forEach((classification) => {
        const content = {
          db_id: classification?.id,
          value: classification?.value ?? '',
          value_type: 'classification',
          confidence: classification?.confidence ?? '',
          edited: classification?.edited,
          check_digit_passed: true,
          label: this.getIntermodalMarkingRailFieldLabel(classification?.type),
        };
        newSection.values.push(content);
      });
      if (metaObject?.reading) {
        const content = {
          db_id: metaObject.reading.id,
          value: metaObject.reading.value ?? '',
          value_type: 'reading',
          confidence: metaObject.reading.confidence ?? '',
          edited: metaObject.reading.edited,
          check_digit_passed: true,
          check_digit_str: '',
          label: this.getIntermodalMarkingRailFieldLabel(metaObject.reading.type),
        };
        metaObject.reading.validations?.forEach((validation) => {
          if (validation.value === 1) {
            content.check_digit_passed = false;
            content.check_digit_str
            += this.$t('objectCard.checkDigitFailed', { validationType: validation.type, confidence: valueToPercent(content.confidence) });
          }
        });
        newSection.values.push(content);
      }

      return newSection;
    },
    copyMetaObjectId() {
      navigator.clipboard.writeText(this.section.section_id);
    },
    setDetectionAndImages(metaObject, section) {
      const newSection = { ...section };

      let detectionTime = 0;
      let detectionRoi = {};
      let detectionSourceId = '';
      let detectionTypeName = '';
      let additionalStreamImages = [];

      const getImageObject = (image, selectedCriteria) => (
        {
          criteria: selectedCriteria,
          time: detectionTime,
          roi: (image.stream_id === detectionSourceId) ? detectionRoi : null,
          id: image.id,
          stream_id: image.stream_id,
          stream_alias: image.stream_alias ? image.stream_alias : image.stream_name,
          width: image.width,
          height: image.height,
        }
      );

      const processAdditionalStreamImages = (image) => {
        image.criteria_list?.forEach((selectedCriteria) => {
          if (selectedCriteria !== 'additional_stream') return;
          additionalStreamImages.push(getImageObject(image, selectedCriteria));
        });
      };

      const processImages = (image) => {
        image.criteria_list?.forEach((selectedCriteria) => {
          if (selectedCriteria === 'additional_stream') return;
          const imageObject = { ...getImageObject(image, selectedCriteria) };
          imageObject.detectionTypeName = detectionTypeName;
          imageObject.additionalStreamImages = [...additionalStreamImages];
          newSection.images.push(imageObject);
        });
      };

      const processDetection = (metaobjectDetection) => {
        detectionTime = metaobjectDetection.time;
        detectionRoi = {
          x: metaobjectDetection.x,
          y: metaobjectDetection.y,
          width: metaobjectDetection.width,
          height: metaobjectDetection.height,
        };
        detectionSourceId = metaobjectDetection.stream_id;
        additionalStreamImages = [];
        metaobjectDetection.images?.forEach(processAdditionalStreamImages);
        metaobjectDetection.images?.forEach(processImages);
      };
      newSection.images = [];

      metaObject?.classifications?.forEach((classification) => {
        detectionTypeName = this.getStatsTypeName(classification.type);
        classification.detections?.forEach(processDetection);
      });

      if (metaObject.reading) {
        detectionTypeName = this.getStatsTypeName(metaObject.reading.type);
        metaObject.reading.detections?.forEach(processDetection);
      }
      newSection.time = getLocalDateTimeFromUnixTimestamp(Math.floor(metaObject.id / 1000000), true);
      return newSection;
    },
    updateSection() {
      let section = {
        type: '',
        typeConfidence: '',
        icon: '',
        values: [],
      };
      section = this.setSectionTypeAndIcon(this.metaObject, section);
      section = this.setSectionValues(this.metaObject, section);
      section = this.setDetectionAndImages(this.metaObject, section);
      this.section = section;
    },
  },
  beforeMount() {
    this.updateSection();
  },
  watch: {
    metaObject() {
      this.updateSection();
    },
  },
};
</script>
